import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import format from 'date-fns/format'
import isValid from 'date-fns/isValid'
import parse from 'date-fns/parse'
import React, { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type SyntheticEvent } from 'react'

import { FRONTEND_TIME_FORMAT_DATE_FNS } from 'settings/constants/date'
import { Z_INDEX } from 'theme/zIndex'

import StyledTextField from './StyledTextField'

import type { SxProps, Theme } from '@mui/material/styles'

// A fixed reference date keeps a parsed time-of-day deterministic — only the
// clock part is ever read back out.
const REFERENCE_DATE = new Date(2000, 0, 1)

// The FE format first, then the shapes a stored or pasted value can arrive in
// (24-hour, unpadded hour) so an existing record still opens for editing.
const ACCEPTED_FORMATS = [FRONTEND_TIME_FORMAT_DATE_FNS, 'h:mm a', 'HH:mm', 'H:mm']

export const parseTimeText = (text: string | null | undefined): Date | null => {
  const trimmed = typeof text === 'string' ? text.trim() : ''
  if (!trimmed) return null
  const parsed = ACCEPTED_FORMATS.map(accepted => parse(trimmed, accepted, REFERENCE_DATE)).find(isValid)
  return parsed ?? null
}

export const formatTimeText = (date: Date | null): string => (date && isValid(date) ? format(date, FRONTEND_TIME_FORMAT_DATE_FNS) : '')

// A logged time of day is nearly always a quarter-hour, so the field offers the
// whole day as a pick list: one click beats placing a cursor inside the mask, and
// the office keys a week of them at a sitting. Keying stays open alongside the
// list, for the times that fall off it.
const TIME_STEP_MIN = 15
const MINUTES_IN_DAY = 24 * 60
const LAST_OPTION_MIN = MINUTES_IN_DAY - TIME_STEP_MIN

const timeOfDay = (minutesFromMidnight: number): Date => {
  const date = new Date(REFERENCE_DATE)
  date.setMinutes(minutesFromMidnight)
  return date
}

const QUARTER_HOURS = Array.from({ length: MINUTES_IN_DAY / TIME_STEP_MIN }, (_, index) => {
  const minutes = index * TIME_STEP_MIN
  return { minutes, label: formatTimeText(timeOfDay(minutes)) }
})

// Where an empty field's list opens: a work day starts in the early morning, and
// midnight-at-the-top would leave every plausible option below the fold.
const EMPTY_FIELD_ANCHOR_MIN = 6 * 60

// The list reads a step down from the field it drops out of, the way a dense
// picker does everywhere else on these surfaces.
const OPTION_SX = { typography: 'subtitle2', color: 'inherit' }

// The list is portaled to the body, so it has to clear whatever hosts the field —
// a popover, a drawer, or a dialog.
const LIST_SX = { zIndex: Z_INDEX.MENU }

const TIME_FIELD_SLOTS = { textField: StyledTextField }

const minutesOf = (date: Date | null): number | null => (date && isValid(date) ? date.getHours() * 60 + date.getMinutes() : null)

// The option the list scrolls to — the field's own time, or the nearest
// quarter-hour to a time that isn't one.
const anchorMinutesOf = (minutes: number | null): number => {
  if (minutes == null) return EMPTY_FIELD_ANCHOR_MIN
  return Math.min(Math.round(minutes / TIME_STEP_MIN) * TIME_STEP_MIN, LAST_OPTION_MIN)
}

// The list highlights only the exact time — a logged time that isn't a
// quarter-hour must not read as the option it opened next to — and it centers what
// it opened on rather than merely bringing it into view, since 96 options don't fit
// the panel.
const TimeOption = ({
  minutes,
  label,
  selected,
  anchored,
  onPick,
}: {
  minutes: number
  label: string
  selected: boolean
  anchored: boolean
  onPick: (picked: number) => void
}) => {
  const handleClick = useCallback(() => onPick(minutes), [minutes, onPick])
  // Prevent the mousedown default so picking doesn't blur the field — a grid edit
  // cell ends its edit on the focus leaving it.
  const handleMouseDown = useCallback((event: SyntheticEvent) => event.preventDefault(), [])
  const centerAnchored = useCallback(
    (node: HTMLLIElement | null) => {
      if (anchored) node?.scrollIntoView({ block: 'center' })
    },
    [anchored]
  )

  return (
    <MenuItem ref={centerAnchored} selected={selected} sx={OPTION_SX} onClick={handleClick} onMouseDown={handleMouseDown}>
      {label}
    </MenuItem>
  )
}

export type HHTimeFieldProps = {
  // Clock text rather than a Date: every consumer so far stores a time of day, not
  // an instant, and a string round-trips through the API and Redux unchanged.
  value: string | null | undefined
  onChange: (value: string) => void
  label?: string
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  variant?: 'filled' | 'outlined' | 'standard'
  autoFocus?: boolean
  // Opens the pick list as soon as the field takes focus. A grid edit cell needs
  // it: its editor mounts already focused, so the click that opened the cell never
  // reaches the field. A field in a form is tabbed through on the way to another
  // one, so there the list waits to be clicked.
  openOnFocus?: boolean
  sx?: SxProps<Theme>
  // TextField-level props (InputProps, inputProps, data attributes) go through
  // `slotProps.textField` — MUI X owns the field's own InputProps/inputProps and
  // silently replaces anything passed at the top level.
  slotProps?: Record<string, unknown>
  inputRef?: React.Ref<HTMLInputElement>
  onBlur?: (event: React.FocusEvent) => void
}

/**
 * Time-of-day input: a masked hh:mm aa field with no clock popup, so the whole
 * value is keyable and each section steps with Up/Down. Clicking it drops the day
 * as a quarter-hour pick list (see `QUARTER_HOURS`).
 *
 * A COMPLETE time commits as soon as it's keyed; an incomplete one waits for blur.
 * MUI reports a half-typed value exactly as a cleared field (both arrive as null),
 * so emitting every null would blank a stored time on the way to a new one. Blur is
 * what settles the genuine clear — and committing a complete time without waiting
 * for it keeps a Save button gated on "dirty" reachable.
 */
const HHTimeField = ({ value, onChange, size = 'small', disabled = false, onBlur, openOnFocus = false, ...rest }: HHTimeFieldProps) => {
  const parsed = useMemo(() => parseTimeText(value), [value])
  // Mirrors the prop, so an external change (an undo in the sheet) still lands.
  // Typing never changes the prop, so it can't clobber a half-typed value.
  const [draft, setDraft] = useState<Date | null>(parsed)
  const [open, setOpen] = useState(false)
  // The whole field, so the list opens under it at its width rather than under the
  // input inside its border.
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => setDraft(parsed), [parsed])

  // Comparing the normalized text on both sides keeps an untouched field — or one
  // keyed back to the time it already held — from emitting at all.
  const commit = useCallback(
    (next: Date | null) => {
      const text = formatTimeText(next)
      if (text !== formatTimeText(parsed)) onChange(text)
    },
    [onChange, parsed]
  )

  const handleChange = useCallback(
    (next: Date | null) => {
      setDraft(next)
      if (next && isValid(next)) commit(next)
    },
    [commit]
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      // Leaving the field settles a clear, and an abandoned half-entry with it. The
      // list goes with it: picking an option holds the focus (see `TimeOption`), so
      // a blur means the office has moved on.
      setOpen(false)
      commit(draft)
      onBlur?.(event)
    },
    [commit, draft, onBlur]
  )

  const openList = useCallback(() => {
    if (!disabled) setOpen(true)
  }, [disabled])
  const handleFocus = useCallback(() => {
    if (openOnFocus && !disabled) setOpen(true)
  }, [disabled, openOnFocus])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      // Escape puts the list away without reaching whatever owns the field — a grid
      // cell takes it as cancelling the whole edit, a popover as closing itself.
      // Arrows are left alone: the mask steps its own sections with them.
      if (event.key !== 'Escape' || !open) return
      event.stopPropagation()
      setOpen(false)
    },
    [open]
  )

  const pick = useCallback(
    (minutes: number) => {
      const picked = timeOfDay(minutes)
      setDraft(picked)
      commit(picked)
      setOpen(false)
    },
    [commit]
  )

  const valueMinutes = minutesOf(parsed)
  const anchorMinutes = anchorMinutesOf(valueMinutes)

  return (
    <>
      <TimeField
        ref={rootRef}
        disabled={disabled}
        // One <input> holding the whole value, rather than MUI X v8's default
        // per-section spans. Every other field in the app is an input, and a grid
        // edit cell focuses and selects one — the section-span structure gives it
        // nothing to focus.
        enableAccessibleFieldDOMStructure={false}
        format={FRONTEND_TIME_FORMAT_DATE_FNS}
        size={size}
        // Without this the field renders a stock TextField and stands 8px taller than every HH field beside it.
        slots={TIME_FIELD_SLOTS}
        value={draft}
        onBlur={handleBlur}
        onChange={handleChange}
        onClick={openList}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        {...rest}
      />
      <Popper anchorEl={rootRef.current} open={open} placement="bottom-start" sx={LIST_SX}>
        <Paper
          sx={{
            mt: 0.5,
            minWidth: rootRef.current?.clientWidth ?? 0,
            maxWidth: 320,
            maxHeight: 208,
            overflowY: 'auto',
            backgroundImage: 'none',
          }}
        >
          <MenuList dense>
            {QUARTER_HOURS.map(option => (
              <TimeOption
                key={option.minutes}
                anchored={option.minutes === anchorMinutes}
                label={option.label}
                minutes={option.minutes}
                selected={option.minutes === valueMinutes}
                onPick={pick}
              />
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </>
  )
}

export default HHTimeField
